import { useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";

import { apiClient, formatNumber } from "@/features/shared";
import {
  CustomDateRangePicker,
  ProductVariantNameCell,
  useSnackbar,
} from "@/features/ui";

export const PurchaseOrderProductVariantSection = () => {
  const { control, getValues, setValue } = useFormContext();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().subtract(1, "day"),
    endDate: dayjs(),
  });

  const { replace } = useFieldArray({
    control,
    name: "purchaseOrderProductVariants",
  });

  const purchaseOrderProductVariants = useWatch({
    control,
    name: "purchaseOrderProductVariants",
  });

  const watchedPurchaseOrderProductVariants = useWatch({
    control,
    name: "purchaseOrderProductVariants",
  });

  const subtotal = useMemo(
    () =>
      watchedPurchaseOrderProductVariants
        .filter((variant) => variant.isSelected)
        .reduce(
          (sum, variant) =>
            sum + (variant.quantity * variant.costPerUnit - variant.discount),
          0
        ),
    [watchedPurchaseOrderProductVariants]
  );

  useEffect(() => {
    setValue("subtotal", subtotal);
  }, [subtotal, setValue]);

  const fetchSalesData = useCallback(async () => {
    const buyer = getValues("buyer");
    if (!buyer?.id || !dateRange.startDate || !dateRange.endDate) return;

    setIsLoading(true);
    try {
      const {
        data: { data },
      } = await apiClient.get(
        `outlets/${buyer.id}/sales-order-product-variants`,
        {
          params: {
            start_date: dateRange.startDate.format("YYYY-MM-DD"),
            end_date: dateRange.endDate.format("YYYY-MM-DD"),
          },
        }
      );

      const mappedVariants = data.map((item) => ({
        sku: item.sku,
        name: item.name,
        soldQuantity: item.quantity,
        stock: item.stock,
        unreceivedOrderedQuantity: item.unreceivedOrderedQuantity,
        product: item.product,
        quantity: 0,
        costPerUnit: item.distributorPrice,
        discount: 0,
        note: null,
        isSelected: false,
      }));

      // Initialize form with all variants
      replace(mappedVariants);
    } catch (error) {
      console.error("Error fetching sales data:", error);
      replace([]);
    } finally {
      setIsLoading(false);
    }
  }, [getValues, dateRange, replace]);

  useEffect(() => {
    fetchSalesData();
  }, [fetchSalesData]);

  const columns = [
    {
      field: "name",
      headerName: "Nama",
      flex: 4,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} />
      ),
    },
    {
      field: "soldQuantity",
      headerName: "Terjual",
      flex: 1,
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "stock",
      headerName: "Stok",
      flex: 1,
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "unreceivedOrderedQuantity",
      headerName: "Pending",
      flex: 1,
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "quantity",
      headerName: "Pesan",
      flex: 1,
      type: "number",
      valueFormatter: (value) => formatNumber(value),
      editable: true,
    },
    {
      field: "unitOfMeasurement",
      headerName: "Satuan",
      flex: 1,
      sortable: false,
      valueGetter: (_, row) => row.product?.unitOfMeasurement?.name || "-",
    },
    {
      field: "costPerUnit",
      headerName: "@Harga",
      flex: 1,
      type: "number",
      valueFormatter: (value) => formatNumber(value),
      editable: true,
    },
  ];

  const handleProcessRowUpdate = (newRow) => {
    // 1) Coerce
    const quantity = Number(newRow.quantity);
    const costPerUnit = Number(newRow.costPerUnit);

    // 2) Validate type/finite
    if (!Number.isFinite(quantity)) {
      throw new Error("Kuantitas tidak valid");
    }
    if (!Number.isFinite(costPerUnit)) {
      throw new Error("Harga tidak valid");
    }

    // 3) Business rules
    if (costPerUnit < 0) {
      throw new Error("Harga tidak boleh minus");
    }

    if (quantity < 0) {
      throw new Error("Kuantitas tidak boleh minus");
    }

    // 4) Update form data directly
    const idx = purchaseOrderProductVariants.findIndex(
      (v) => v.sku === newRow.sku
    );
    if (idx !== -1) {
      setValue(`purchaseOrderProductVariants.${idx}.quantity`, quantity, {
        shouldDirty: true,
      });
      setValue(`purchaseOrderProductVariants.${idx}.costPerUnit`, costPerUnit, {
        shouldDirty: true,
      });
    }

    return { ...newRow, quantity, costPerUnit };
  };

  // Update isSelected based on checkbox selection
  const handleRowSelectionChange = useCallback(
    (newSelection) => {
      const currentVariants = getValues("purchaseOrderProductVariants");
      const selectedIds = newSelection.ids || new Set();

      currentVariants.forEach((variant, idx) => {
        // Handle both 'include' and 'exclude' selection types
        const isSelected =
          newSelection.type === "include"
            ? selectedIds.has(variant.sku)
            : !selectedIds.has(variant.sku);

        // Only update if selection state changed
        if (variant.isSelected !== isSelected) {
          setValue(
            `purchaseOrderProductVariants.${idx}.isSelected`,
            isSelected,
            { shouldDirty: false }
          );
          setValue(
            `purchaseOrderProductVariants.${idx}.quantity`,
            isSelected ? variant.soldQuantity : 0,
            { shouldDirty: true }
          );
        }
      });
    },
    [getValues, setValue]
  );

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between"
          }}>
          <Typography variant="h6">
            Pilih Produk Varian untuk Dipesan
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={fetchSalesData}
            disabled={isLoading}
          >
            Refresh Data
          </Button>
        </Stack>

        <CustomDateRangePicker
          dateRange={dateRange}
          onChanged={setDateRange}
          minStartDate={dayjs("2025-09-14")}
        />

        <Box sx={{ height: 400, width: "100%" }}>
          <DataGrid
            rows={purchaseOrderProductVariants}
            columns={columns}
            loading={isLoading}
            getRowId={(row) => row.sku}
            isCellEditable={(params) => params.row.isSelected}
            hideFooter
            checkboxSelection
            disableRowSelectionOnClick
            onRowSelectionModelChange={handleRowSelectionChange}
            processRowUpdate={handleProcessRowUpdate}
            onProcessRowUpdateError={(error) => {
              showSnackbar(error.message);
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: "name", sort: "asc" }],
              },
            }}
          />
        </Box>

        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Centang produk varian yang ingin dipesan, lalu klik pada kolom
          &quot;Pesan&quot; untuk mengedit jumlah yang akan dipesan.
        </Typography>
      </Stack>
    </Paper>
  );
};
